// quota-reset-time-parser.ts
//
// Split out of quota-sleep-scheduler.ts (06g R8, debate B7 — 200-line
// budget). Parses the provider's "resets …" clause and turns it into an
// instant. Two readings, chosen by the text itself:
//   - with an IANA zone in parentheses — "resets 5pm (Asia/Saigon)", the
//     verbatim dogfood-6 string — the wall-clock is in THAT zone (17:00 ICT =
//     10:00Z); the UTC-only reading used to sleep 7 h too long;
//   - without one, the pre-R8 behaviour: the hour is UTC (§6.4).
// `Intl.DateTimeFormat` only — no dependency; an unknown zone throws
// RangeError there, which callers treat as "not parseable" (backoff ladder).
const RESET_TIME_PATTERN =
  /resets?\s+(?:at\s+)?(\d{1,2})(?::(\d{2}))?\s*(am|pm)?\s*([A-Za-z]{2,5})?\s*(?:\(([A-Za-z_]+\/[A-Za-z_+-]+)\))?/i;

export interface ParsedResetTime {
  hour: number;
  minute: number;
  /** IANA zone the provider named in parentheses; undefined ⇒ UTC reading. */
  zone: string | undefined;
}

export function parseResetTime(text: string | undefined): ParsedResetTime | undefined {
  const match = text?.match(RESET_TIME_PATTERN);
  if (!match) return undefined;
  let hour = Number(match[1]);
  const minute = match[2] ? Number(match[2]) : 0;
  const meridiem = match[3]?.toLowerCase();
  if (meridiem === "pm" && hour < 12) hour += 12;
  if (meridiem === "am" && hour === 12) hour = 0;
  if (hour < 0 || hour > 23 || minute < 0 || minute > 59) return undefined;
  return { hour, minute, zone: match[5] };
}

/** Pre-R8 reading: the hour is UTC; already past today ⇒ tomorrow (UTC). */
export function resetInstantUtc(now: Date, reset: ParsedResetTime): Date {
  const wakeAt = new Date(now);
  wakeAt.setUTCHours(reset.hour, reset.minute, 0, 0);
  if (wakeAt.getTime() <= now.getTime()) wakeAt.setUTCDate(wakeAt.getUTCDate() + 1);
  return wakeAt;
}

/** Zone's UTC offset (ms) at `instant`, from `formatToParts` — DST-correct per instant. */
function zoneOffsetMs(instant: Date, zone: string): number {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: zone,
    hourCycle: "h23",
    year: "numeric",
    month: "numeric",
    day: "numeric",
    hour: "numeric",
    minute: "numeric",
    second: "numeric",
  }).formatToParts(instant);
  const n = (type: string) => Number(parts.find((p) => p.type === type)?.value);
  const asUtc = Date.UTC(n("year"), n("month") - 1, n("day"), n("hour"), n("minute"), n("second"));
  return asUtc - instant.getTime();
}

/** Next occurrence of `reset` wall-clock in `zone` strictly after `now`; undefined for an unknown zone. */
export function resetInstantInZone(
  now: Date,
  reset: ParsedResetTime,
  zone: string,
): Date | undefined {
  try {
    const offset = zoneOffsetMs(now, zone);
    const localNow = new Date(now.getTime() + offset);
    const localWall = Date.UTC(
      localNow.getUTCFullYear(),
      localNow.getUTCMonth(),
      localNow.getUTCDate(),
      reset.hour,
      reset.minute,
    );
    let wakeAt = new Date(localWall - offset);
    if (wakeAt.getTime() <= now.getTime()) {
      const tomorrow = localWall + 24 * 60 * 60 * 1000;
      wakeAt = new Date(tomorrow - zoneOffsetMs(new Date(tomorrow - offset), zone));
    }
    return wakeAt;
  } catch {
    return undefined; // RangeError: not an IANA zone
  }
}
